import { StatusPill } from '../components/StatusPill';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, Clock, Loader2, PauseCircle, Plus } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { DataTable, type Column } from '../components/DataTable';
import { Kanban, type KanbanColumn } from '../components/Kanban';
import { Modal } from '../components/Modal';
import { SelectionBar } from '../components/SelectionBar';
import { useToast } from '../components/Toast';
import { EmptyState } from '../components/EmptyState';
import { HelpHint } from '../components/HelpHint';
import { SlaBadge, SlaBar } from '../components/SlaBadge';
import { PriorityBadge } from '../components/StatusBadge';
import { api, ApiError } from '../lib/api';
import { actionToReach, choosableActionToReach, NEEDS_REASON } from '@jt/domain';
import { useSession } from '../lib/session';
import type { TicketSummary } from '../lib/types';

/**
 * The statuses that genuinely have nobody on them.
 *
 * Separate from the buckets below, and it has to be. The summary line says
 * "N need dispatch", which used to be computed from whichever statuses
 * happened to be in the first bucket — fine while that bucket was exactly the
 * undispatched ones, and wrong the moment "To do" was widened to include
 * ASSIGNED. A ticket with a technician's name on it is not waiting for a
 * dispatcher, and a header that said otherwise would send somebody looking for
 * work that is already moving.
 *
 * The filter answers "show me this part of the board". This answers "is
 * anything waiting for me". Two questions, two lists.
 */
const NEEDS_DISPATCH = ['NEW', 'TRIAGED', 'DISPATCHING', 'DISPATCH_FAILED'];

/**
 * How urgent each priority is, as a number.
 *
 * Sorting by the label would put P1 next to P2 by luck and then break the day
 * somebody renames one to "Critical" — "Critical" sorts before "Normal", which
 * is right, and before "Low", which is not. A rank says what the order means
 * rather than relying on the alphabet to agree with it.
 */
const URGENCY: Record<string, number> = { P1: 1, P2: 2, P3: 3, P4: 4 };

/**
 * The dispatcher's day on one screen — PHASES.md Phase 7.
 *
 * ## Why this stopped being a kanban
 *
 * It was five columns of cards, and it worked at a dozen tickets. What it could
 * not do was grow. A card spends most of its area on padding and repeats every
 * label once per ticket, so a column of forty is a column nobody reads to the
 * bottom of — and the fortieth ticket is exactly the one that has been waiting
 * longest. Worse, the board fetched every open ticket and grouped them in the
 * browser, under a `take: 200` in the API with no page after it. At 201 open
 * tickets the two hundred and first was simply absent from the screen, silently.
 *
 * A table fixes both. The buckets survive as a filter rather than as columns,
 * because "what needs dispatch?" is a question worth one click; and the rows
 * are paged, so the list is bounded by the page rather than by a cap.
 *
 * ## What the buckets are
 *
 * Five stages in the words the business uses, covering all seventeen statuses
 * between them — so every ticket is somewhere and nothing falls off the board
 * by being in a status no bucket claimed.
 *
 * `Closed` is the one that earns a second look. It is the only bucket holding
 * work that is finished rather than outstanding, and it exists because the
 * ticket list returns closed tickets whether or not anybody wants them: before
 * this they were visible under "Every stage" and reachable through no filter
 * at all, which made the board's own total look wrong.
 */
/**
 * One stage of the board.
 *
 * `statuses` is the usual case. `match` exists for the one bucket that is not
 * about a status at all — where the ticket came from — and a bucket that
 * answers a different question needs a different test rather than a status
 * list bent to fit.
 */
type Bucket = {
  key: string;
  label: string;
  statuses?: string[];
  match?: (ticket: TicketSummary) => boolean;
};

const BUCKETS: Bucket[] = [
  /*
   * From the website, and first.
   *
   * These arrive with nobody behind them: no dispatcher typed them, no
   * customer is on the phone, and until somebody opens one nobody knows
   * whether it is a sales lead, a complaint or a fault. They were
   * indistinguishable from staff-raised work in the queue, which is how a
   * quote request sat for two days behind tickets somebody was already
   * handling.
   *
   * First because the website now has no email fallback — a submission that
   * is missed here is missed everywhere.
   *
   * Only the ones still waiting. A website ticket that has been picked up is
   * ordinary work and belongs in the stage it has reached, or this bucket
   * would grow for ever and stop meaning "needs a look".
   */
  {
    key: 'website',
    label: 'From the website',
    match: (ticket) =>
      ticket.source === 'website' &&
      ['DRAFT', 'NEW', 'TRIAGED', 'DISPATCHING', 'DISPATCH_FAILED'].includes(ticket.status),
  },
  {
    key: 'todo',
    label: 'To do',
    /*
     * DRAFT is in here because it is literally to do: a ticket somebody is
     * still filling in, one 'submit' away from NEW. It belonged to no bucket
     * at all before, which meant it counted under 'Every stage' and could not
     * be reached by any filter — the same quiet inconsistency that left closed
     * tickets stranded.
     */
    statuses: ['DRAFT', 'NEW', 'TRIAGED', 'DISPATCHING', 'DISPATCH_FAILED', 'ASSIGNED'],
  },
  { key: 'working', label: 'In progress', statuses: ['EN_ROUTE', 'ON_SITE', 'IN_PROGRESS'] },
  {
    key: 'impediment',
    label: 'Impediment',
    statuses: ['ON_HOLD', 'PENDING_CUSTOMER', 'PENDING_ACCESS', 'PENDING_PARTS'],
  },
  { key: 'done', label: 'Done', statuses: ['WORK_DONE', 'VERIFIED'] },
  { key: 'closed', label: 'Closed', statuses: ['CLOSED', 'CANCELLED'] },
];

export function BoardPage() {
  const { can, user } = useSession();
  const navigate = useNavigate();
  const [bucket, setBucket] = useState('');
  const [page, setPage] = useState(1);
  /*
   * Ids rather than rows, so a selection survives the list being refetched —
   * and this board refetches every ten seconds. Holding rows would mean the
   * ticks quietly detaching from the tickets they were made against.
   */
  const [selected, setSelected] = useState<Set<string>>(new Set());

  /*
   * Board or list, remembered.
   *
   * Which of the two somebody wants is a matter of the job they do rather than
   * the moment: a dispatcher lives on the board, an administrator reconciling
   * the month lives in the list. Asking again every morning is asking the same
   * person the same question for ever.
   */
  const [view, setView] = useState<'board' | 'list'>(() => {
    try {
      return localStorage.getItem('gsn-board-view') === 'list' ? 'list' : 'board';
    } catch {
      return 'board';
    }
  });

  const chooseView = (next: 'board' | 'list') => {
    setView(next);
    try {
      localStorage.setItem('gsn-board-view', next);
    } catch {
      /* A private window. It simply is not remembered. */
    }
  };

  /** Which kind of work is shown. Empty means all of it. */
  const [kind, setKind] = useState('');
  /** The card being moved, so it can say so while the request is in flight. */
  const [moving, setMoving] = useState<string | null>(null);

  /*
   * A move waiting on a reason.
   *
   * `window.prompt` did this first and was wrong twice over: it blocks the
   * whole page — the board's own refresh included — and it is the one dialog
   * in this application that looks like nothing else in it. It also made the
   * path untestable, because a blocking prompt hangs a headless browser
   * outright, which is how this was found.
   */
  const [askingReason, setAskingReason] = useState<{
    ticket: TicketSummary;
    column: KanbanColumn<TicketSummary>;
    event: string;
    /** When the column is several statuses and the person picks which. */
    chooseFrom?: string[];
  } | null>(null);
  const [reason, setReason] = useState('');
  const [holdStatus, setHoldStatus] = useState('');
  const toast = useToast();
  const queryClient = useQueryClient();

  /*
   * One status per request when a bucket is chosen.
   *
   * The API filters on a single status, and a bucket is up to four of them. So
   * the bucket is applied here, over the page the server sent — which is honest
   * only because the pager reports the server's total, not the filtered length.
   * Filtering a page and then counting it would produce a pager that disagrees
   * with the rows above it, which is worse than no pager.
   */
  const tickets = useQuery({
    queryKey: ['tickets', 'board', page],
    queryFn: () => api.tickets({ page, perPage: 25 }),
    // ARCHITECTURE.md §2: the board changes a few times an hour at this volume.
    refetchInterval: 10_000,
    placeholderData: (previous) => previous,
  });

  if (tickets.isPending) {
    return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin text-primary-500" />;
  }

  const everything = tickets.data?.tickets ?? [];

  /*
   * The kinds of work on the board right now, taken from the tickets rather
   * than from the catalogue.
   *
   * A business with forty job types has four on the board on a given morning,
   * and a filter listing the other thirty-six is a filter somebody has to read
   * before they can use it. This lists what is actually there.
   */
  const kinds = [...new Set(everything.map((ticket) => ticket.jobTypeName))].sort((a, b) =>
    a.localeCompare(b),
  );

  const all = kind ? everything.filter((ticket) => ticket.jobTypeName === kind) : everything;
  const chosen = BUCKETS.find((candidate) => candidate.key === bucket);
  const rows = chosen
    ? all.filter((ticket) =>
        chosen.match ? chosen.match(ticket) : (chosen.statuses ?? []).includes(ticket.status),
      )
    : all;

  /*
   * Said in the summary line as well as offered as a filter.
   *
   * A filter nobody selects shows nothing, and the whole reason this bucket
   * exists is that website submissions were being missed. A count in the
   * header is seen without choosing to look.
   */
  const fromWebsite = all.filter((ticket) =>
    BUCKETS[0]!.match ? BUCKETS[0]!.match(ticket) : false,
  ).length;

  const needDispatch = all.filter((ticket) =>
    NEEDS_DISPATCH.includes(ticket.status),
  ).length;
  const breached = all.filter((ticket) => ticket.slaBreached).length;

  /**
   * The board's columns, from the same buckets the list filters by.
   *
   * One definition, so the two views can never disagree about which stage a
   * ticket is in — which is the failure that makes somebody stop trusting a
   * board and go back to the list.
   */
  /*
   * What each action is called in the words the business uses.
   *
   * The event names are the state machine's vocabulary — `start_travel`,
   * `dispatch_failed` — and they are exactly right there and exactly wrong on
   * a card. Anything not named here falls back to the raw event rather than
   * being hidden: a move nobody has worded yet should still be possible.
   */
  /** The four kinds of waiting, in the words the business uses. */
  const HOLD_WORDING: Record<string, string> = {
    ON_HOLD: 'Nothing in particular — just paused',
    PENDING_CUSTOMER: 'The customer',
    PENDING_ACCESS: 'Access to the site',
    PENDING_PARTS: 'Parts',
  };

  const TRANSITION_WORDING: Record<string, string> = {
    submit: 'submit it',
    triage: 'triage it',
    dispatch: 'send it out',
    assign: 'assign it',
    start_travel: 'on the way',
    arrive: 'arrived',
    start_work: 'start work',
    hold: 'put on hold',
    resume: 'resume it',
    verify: 'verify it',
    close: 'close it',
    cancel: 'cancel it',
    reopen: 'reopen it',
  };

  const kanbanColumns: KanbanColumn<TicketSummary>[] = BUCKETS.map((bucket) => ({
    key: bucket.key,
    label: bucket.label,
    holds: (ticket) =>
      bucket.match ? bucket.match(ticket) : (bucket.statuses ?? []).includes(ticket.status),
    note: bucket.key === 'website' ? 'Nobody has picked these up yet' : undefined,
  }));

  /**
   * What moving a card to a column would actually do.
   *
   * Asked of the transition table rather than decided here, so the board
   * offers exactly the moves the server will accept — and a card cannot be
   * dropped somewhere that produces a refusal.
   *
   * The first bucket is not a stage at all: "From the website" is about where
   * a ticket came from, and no action moves a ticket *into* it. So nothing can
   * be dropped there, which is correct and also what it looks like.
   */
  const actionFor = (ticket: TicketSummary, column: KanbanColumn<TicketSummary>) => {
    const bucket = BUCKETS.find((one) => one.key === column.key);
    if (!bucket?.statuses) return null;

    /*
     * The transition table's actors are the ones that can fire events — admin,
     * dispatcher, technician, system. A role outside that set holds no
     * transitions at all, and passing it through would be asking the table a
     * question it has no row for.
     */
    const actor =
      user?.role === 'admin' || user?.role === 'dispatcher' || user?.role === 'technician'
        ? user.role
        : undefined;

    const from = ticket.status as Parameters<typeof actionToReach>[0];

    const event = actionToReach(from, bucket.statuses, actor);
    if (event) {
      return { event, label: `${column.label} — ${TRANSITION_WORDING[event] ?? event}` };
    }

    /*
     * And the moves where the person supplies the destination.
     *
     * "Impediment" is four statuses, not one, so no single action lands on it
     * — which made the column unreachable by dragging at all. It is reachable
     * now, and the ambiguity becomes the question it always was: waiting on
     * what?
     */
    const choosable = choosableActionToReach(from, bucket.statuses, actor);
    return choosable
      ? {
          event: choosable.event,
          label: `${column.label} — ${TRANSITION_WORDING[choosable.event] ?? choosable.event}`,
          chooseFrom: choosable.chooseFrom,
        }
      : null;
  };

  const move = async (
    ticket: TicketSummary,
    column: KanbanColumn<TicketSummary>,
    event: string,
  ) => {
    /*
     * Some moves cannot be made silently.
     *
     * Holding, cancelling and reopening are refused without a reason, and the
     * reason is the whole point of them — "the system put it on hold" is not
     * an answer anybody should have to give a customer. A drag has nowhere to
     * type one, so it is asked for before the request rather than discovered
     * as a refusal afterwards.
     */
    const bucket = BUCKETS.find((one) => one.key === column.key);
    const choosable = bucket?.statuses
      ? choosableActionToReach(
          ticket.status as Parameters<typeof actionToReach>[0],
          bucket.statuses,
        )
      : null;
    const chooseFrom = choosable?.event === event ? choosable.chooseFrom : undefined;

    if ((NEEDS_REASON as readonly string[]).includes(event) || chooseFrom) {
      setReason('');
      setHoldStatus(chooseFrom?.[0] ?? '');
      setAskingReason({ ticket, column, event, chooseFrom });
      return;
    }

    await fire(ticket, column, event);
  };

  /** The request itself, once there is nothing left to ask. */
  const fire = async (
    ticket: TicketSummary,
    column: KanbanColumn<TicketSummary>,
    event: string,
    why?: string,
    /** Which of a column's statuses to land on, when the person chose. */
    landOn?: string,
  ) => {
    setMoving(ticket.id);
    try {
      await api.transition(ticket.id, event, {
        ...(why ? { reason: why } : {}),
        ...(landOn ? { holdStatus: landOn } : {}),
      });
      await queryClient.invalidateQueries({ queryKey: ['tickets'] });
      toast.done(`${ticket.reference} moved to ${column.label}.`);
    } catch (error) {
      /*
       * Said out loud rather than sprung back silently. A card that returns to
       * where it started with no explanation is indistinguishable from a board
       * that is broken, and the guards this hits — no report, not your job —
       * are exactly the ones somebody needs to be told about.
       */
      toast.failed(
        error instanceof ApiError ? error.message : `${ticket.reference} could not be moved.`,
      );
    } finally {
      setMoving(null);
    }
  };

  const columns: Column<TicketSummary>[] = [
    {
      key: 'reference',
      header: 'Ticket',
      /* Numerically, so JOB-9 comes before JOB-10 rather than after it. */
      sortBy: (ticket) => ticket.reference,
      cell: (ticket) => (
        <div className="min-w-0">
          <span className="font-medium text-primary-600">{ticket.reference}</span>
          <p className="truncate text-xs text-muted-foreground sm:hidden">{ticket.title}</p>
        </div>
      ),
    },
    {
      key: 'title',
      header: 'What it is',
      secondary: true,
      sortBy: (ticket) => ticket.title.toLowerCase(),
      cell: (ticket) => (
        <div className="flex flex-wrap items-center gap-1">
          <span className="truncate">{ticket.title}</span>
          {ticket.isIncident && (
            <span className="badge bg-danger-50 text-danger-600">
              incident · {ticket.childCount}
            </span>
          )}
          {ticket.onHoldReason && (
            <span className="badge bg-accent-50 text-accent-600" title={ticket.onHoldReason}>
              <PauseCircle className="h-3 w-3" aria-hidden="true" />
              paused
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      /* Most urgent first when ascending, which is what "sort by priority"
         means to everybody except a dictionary. */
      sortBy: (ticket) => URGENCY[ticket.priority] ?? 99,
      cell: (ticket) => <PriorityBadge priority={ticket.priority} />,
    },
    {
      key: 'where',
      header: 'Where',
      secondary: true,
      sortBy: (ticket) => ticket.areaName ?? null,
      cell: (ticket) =>
        ticket.areaName ? (
          <span>
            {ticket.areaName}
            {ticket.zoneCode && (
              <span className="ml-1 text-xs text-muted-foreground">{ticket.zoneCode}</span>
            )}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'assignee',
      header: 'Who',
      /*
       * Null for unassigned, so those sink to the bottom rather than sorting
       * under whatever letter "Unassigned" happens to start with. Finding the
       * unassigned ones is what the bucket filter is for; this column is for
       * finding a person.
       */
      sortBy: (ticket) => ticket.assignee?.name ?? null,
      cell: (ticket) =>
        ticket.assignee ? (
          <span className="truncate">{ticket.assignee.name}</span>
        ) : (
          <span className="font-medium text-accent-600">Unassigned</span>
        ),
    },
    {
      key: 'age',
      header: 'Age',
      numeric: true,
      secondary: true,
      /* The raw timestamp. Sorting the rendered "3h" would order 3h, 30h, 4h. */
      sortBy: (ticket) => new Date(ticket.createdAt).getTime(),
      cell: (ticket) => {
        const hours = Math.round((Date.now() - new Date(ticket.createdAt).getTime()) / 3_600_000);
        return (
          <span className="inline-flex items-center gap-0.5">
            <Clock className="h-3 w-3" aria-hidden="true" />
            {hours < 1 ? 'now' : `${hours}h`}
          </span>
        );
      },
    },
    {
      key: 'sla',
      header: 'Target',
      /*
       * How far through the target it is, so descending puts the worst
       * breaches at the top. A ticket with no SLA has no position in that
       * order at all, which is what the null is for.
       */
      sortBy: (ticket) => (ticket.slaState ? (ticket.slaPercent ?? 0) : null),
      cell: (ticket) => (
        <div className="min-w-[7rem] space-y-1">
          <SlaBadge
            state={ticket.slaState}
            percent={ticket.slaPercent}
            targetMin={ticket.slaTargetMin}
          />
          {ticket.slaState && ticket.slaState !== 'PAUSED' && (
            <SlaBar state={ticket.slaState} percent={ticket.slaPercent} />
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-1.5 font-heading text-2xl">
            Board
            <HelpHint>
              Everything open, newest and most urgent first. Red means an SLA target has been
              missed; amber means something is paused and the clock is stopped. Use the filter to
              see one stage at a time.
            </HelpHint>
          </h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {needDispatch} need dispatch
            {fromWebsite > 0 && (
              <span className="text-primary-700"> · {fromWebsite} from the website</span>
            )}
            {breached > 0 && <span className="text-danger-600"> · {breached} breached</span>}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {/*
            The five columns, as one control. A dropdown holds any number of
            stages without the page growing sideways, which five columns of
            cards could not.
          */}
          <label className="sr-only" htmlFor="bucket">
            Stage
          </label>
          <select
            id="bucket"
            /* Sized to its longest option rather than to the row. A select with
               `flex-1` stretches across whatever space is left and reads as a
               search box. */
            className="input w-auto min-w-[11rem]"
            value={bucket}
            onChange={(event) => setBucket(event.target.value)}
          >
            <option value="">Every stage</option>
            {BUCKETS.map((candidate) => (
              <option key={candidate.key} value={candidate.key}>
                {candidate.label}
              </option>
            ))}
          </select>

          {/*
            * The kinds of work, from what is actually on the board.
            *
            * The stage dropdown answers "how far along"; this answers "what
            * sort of job" — a new connection and a fault are different work
            * done by different people, and a dispatcher usually wants one of
            * them at a time.
            */}
          <label className="sr-only" htmlFor="kind">
            Kind of work
          </label>
          <select
            id="kind"
            className="input w-auto min-w-[10rem]"
            value={kind}
            onChange={(event) => setKind(event.target.value)}
          >
            <option value="">Every kind of work</option>
            {kinds.map((one) => (
              <option key={one} value={one}>
                {one}
              </option>
            ))}
          </select>

          {/* Two views of one board. */}
          <div className="inline-flex overflow-hidden rounded-xl border border-border">
            {(['board', 'list'] as const).map((one) => (
              <button
                key={one}
                type="button"
                aria-pressed={view === one}
                className={`px-3 py-1.5 text-sm ${
                  view === one ? 'bg-primary-600 text-white' : 'hover:bg-muted'
                }`}
                onClick={() => chooseView(one)}
              >
                {one === 'board' ? 'Board' : 'List'}
              </button>
            ))}
          </div>

          {can('ticket:create') && (
            <Link to="/tickets/new" className="btn-primary">
              <Plus className="h-4 w-4" aria-hidden="true" />
              New ticket
            </Link>
          )}
        </div>
      </div>

      {/*
        * The reason a move needs, asked in the application's own dialog.
        *
        * Not a browser prompt: that blocks the page, looks like nothing else
        * here, and cannot be driven by a test — so the one path that changes
        * what a ticket means would be the one path nothing could check.
        */}
      {askingReason && (
        <Modal
          title={`Why is ${askingReason.ticket.reference} moving to ${askingReason.column.label}?`}
          subtitle="This is recorded on the ticket, and is what makes the timeline defensible later."
          onClose={() => setAskingReason(null)}
          footer={
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  if (!reason.trim()) return;
                  const pending = askingReason;
                  setAskingReason(null);
                  void fire(
                    pending.ticket,
                    pending.column,
                    pending.event,
                    reason.trim(),
                    pending.chooseFrom ? holdStatus : undefined,
                  );
                }}
              >
                Move it
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setAskingReason(null)}
              >
                Leave it where it is
              </button>
            </div>
          }
        >
          {askingReason.chooseFrom && (
            <label className="mb-3 block text-sm">
              <span className="mb-1 block font-medium">Waiting on what?</span>
              <select
                className="input w-full"
                value={holdStatus}
                aria-label="What the ticket is waiting on"
                onChange={(event) => setHoldStatus(event.target.value)}
              >
                {askingReason.chooseFrom.map((one) => (
                  <option key={one} value={one}>
                    {HOLD_WORDING[one] ?? one}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-muted-foreground">
                This is the most useful thing about a hold — it says who has to do something next.
              </span>
            </label>
          )}

          <label className="block text-sm">
            <span className="mb-1 block font-medium">Reason</span>
            <textarea
              className="input w-full"
              rows={3}
              autoFocus
              value={reason}
              aria-label="Reason for the move"
              placeholder="Waiting on the customer to clear the way to the pole"
              onChange={(event) => setReason(event.target.value)}
            />
            {!reason.trim() && (
              <span className="mt-1 block text-xs text-muted-foreground">
                Needed — the move is refused without it.
              </span>
            )}
          </label>
        </Modal>
      )}

      {view === 'board' ? (
        <Kanban<TicketSummary>
          rows={rows}
          columns={kanbanColumns}
          rowKey={(ticket) => ticket.id}
          actionFor={actionFor}
          onMove={move}
          moving={moving}
          emptyColumn="Nothing at this stage"
          card={(ticket) => (
            <>
              <Link
                to={`/tickets/${ticket.id}`}
                className="font-medium text-primary-600 hover:underline"
              >
                {ticket.reference}
              </Link>
              <p className="mt-0.5 line-clamp-2 text-sm">{ticket.title}</p>
              <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                <PriorityBadge priority={ticket.priority} />
                <span>{ticket.jobTypeName}</span>
                {ticket.assignee && <span>· {ticket.assignee.name}</span>}
                {/*
                  A pill, not a word in a sentence.
                  It is the most operationally important thing on the card and
                  it was set in the same size and rhythm as the job type and
                  the assignee, so it read as one more attribute rather than
                  as the thing to act on. Red text alone was also carrying the
                  meaning; the pill puts a border and a ground behind it so it
                  survives being printed, screenshotted, or seen by somebody
                  who cannot separate red from grey.
                */}
                {ticket.slaBreached && <StatusPill tone="bad">Breached</StatusPill>}
              </p>
            </>
          )}
        />
      ) : (
        <>
      {/*
        Nothing until something is ticked. See SelectionBar for why the bar
        appears rather than sitting there disabled.
      */}
      <SelectionBar
        rows={rows}
        selected={selected}
        onClear={() => setSelected(new Set())}
        rowKey={(ticket) => ticket.id}
        exportName="tickets"
        csv={[
          { header: 'Reference', value: (t) => t.reference },
          { header: 'Title', value: (t) => t.title },
          { header: 'Status', value: (t) => t.status },
          { header: 'Priority', value: (t) => t.priority },
          { header: 'Area', value: (t) => t.areaName },
          { header: 'Zone', value: (t) => t.zoneCode },
          { header: 'Customer', value: (t) => t.customerName },
          { header: 'Assigned to', value: (t) => t.assignee?.name ?? '' },
          { header: 'Source', value: (t) => t.source },
          { header: 'Website form', value: (t) => t.sourceForm ?? '' },
          { header: 'SLA breached', value: (t) => (t.slaBreached ? 'yes' : 'no') },
          { header: 'Raised', value: (t) => t.createdAt },
        ]}
        actions={[
          {
            key: 'archive',
            label: 'Archive these',
            icon: Archive,
            destructive: true,
            confirm: (chosen) =>
              `Archive ${chosen.length} ticket${chosen.length === 1 ? '' : 's'}?\n\n` +
              'They keep their history and can be restored from the ticket itself.',
            run: async (chosen) => {
              /*
               * One at a time, and counted.
               *
               * There is no bulk endpoint, and inventing one that skipped the
               * per-ticket rules would be the wrong kind of shortcut — each
               * archive is audited and each can legitimately refuse. Reporting
               * how many actually went is the honest end to that: forty
               * requests where two failed is not "done".
               */
              let done = 0;
              const failed: string[] = [];
              for (const ticket of chosen) {
                try {
                  await api.archiveTicket(ticket.id, 'Archived in bulk from the board');
                  done += 1;
                } catch {
                  failed.push(ticket.reference);
                }
              }

              setSelected(new Set());
              await queryClient.invalidateQueries({ queryKey: ['tickets'] });

              if (failed.length === 0) {
                toast.done(`${done} archived.`);
              } else {
                toast.failed(
                  `${done} archived. ${failed.length} refused: ${failed.slice(0, 3).join(', ')}` +
                    (failed.length > 3 ? '…' : ''),
                );
              }
            },
          },
        ]}
      />

      <DataTable
        rows={rows}
        selected={selected}
        onSelect={setSelected}
        columns={columns}
        rowKey={(ticket) => ticket.id}
        onRowClick={(ticket) => navigate(`/tickets/${ticket.id}`)}
        caption="Open tickets"
        page={
          tickets.data
            ? {
                page: tickets.data.page,
                perPage: tickets.data.perPage,
                total: tickets.data.total,
                pages: tickets.data.pages,
              }
            : undefined
        }
        onPage={setPage}
        empty={
          <EmptyState
            icon={Clock}
            headline={chosen ? `Nothing ${chosen.label.toLowerCase()}` : 'Nothing open'}
            body={
              chosen
                ? 'Other stages may still have work. Choose "Every stage" to see all of it.'
                : 'Every ticket is closed. New work will appear here.'
            }
            action={
              chosen ? { label: 'Show every stage', onClick: () => setBucket('') } : undefined
            }
          />
        }
      />
        </>
      )}
    </div>
  );
}
